import FormBlock from "@common/controls/Form/Block/component";
import InfiniteScroll from "@common/controls/InfiniteScroll/component";
import { SelectItem, SelectStatic } from "@common/controls/Inputs/Select/Base/component";
import Preloader from "@common/controls/Preloader/component";
import Timeline, { type TimelineAttachment, TimelineGroupped, type TimelineItemInfo } from "@common/controls/Timeline/component";
import { getHtmlId } from "@common/controls/Timeline/lib";
import { type LabelValue, SystemColor } from "@common/declarations";
import { ContentAlign } from "@common/hocs/withDropdown/lib";
import { type WithLazyComponentAddedProps, withLazyHOC } from "@common/hocs/withLazy";
import { useI18n } from "@common/hooks/i18n";
import { identityPromise, scrollToEl } from "@common/lib/misc";
import classNames from "classnames";
import { isUndefined, last } from "lodash";
import React from "react";

import "./style.less";

export interface ActivityFilter<T> {
  title: string;
  onChange: (data: T) => void;
  options: LabelValue<T>[];
}

export interface ActivityItem {
  addon?: any;
  attachments?: TimelineAttachment[];
  className?: string;
  color?: SystemColor;
  date: Date | string;
  description: any;
  icon: any;
  id: string;
  isDateHidden?: boolean;
  onClick?: () => void;
  title: any;
}

export type ActivityProps<T> = Partial<Omit<WithLazyComponentAddedProps<ActivityItem>, "refreshKey">> & {
  actions?: any;
  className?: string;
  color?: SystemColor;
  filter?: ActivityFilter<T> | string;
  footerActions?: any;
  hasLine?: boolean;
  isBorderless?: boolean;
  isDateHidden?: boolean;
  isFullHeight?: boolean;
  isNarrow?: boolean;
  isNoTitle?: boolean;
  isUngrouped?: boolean;
  itemsAddon?: React.JSX.Element;
  noDataMessage?: any;
  /** controls preloader height on first load */
  preloadCount?: number;
  onScroll?: () => void;
  options: ActivityItem[];
  retainScroll?: boolean;
  scrollTo?: string | "last" | null;
  style?: React.CSSProperties;
};

const Activity = <T extends {}>(props: ActivityProps<T>) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const scrollAdjustRef = React.useRef<number>(undefined);

  const i18n = useI18n();

  React.useLayoutEffect(() => {
    if (props.retainScroll && scrollAdjustRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight - scrollAdjustRef.current;
    }

    scrollAdjustRef.current = scrollRef.current ? scrollRef.current.scrollHeight - scrollRef.current.scrollTop : 0;
  }, [props.options, props.retainScroll]);

  const items = React.useMemo(() => {
    return props.options.map<TimelineItemInfo>((x) => ({
      content: x.description,
      className: x.className,
      addon: x.addon,
      onClick: x.onClick,
      attachments: x.attachments,
      icon: x.icon,
      date: x.date,
      title: x.title,
      color: x.color,
      id: x.id,
      isDateHidden: x.isDateHidden
    }));
  }, [props.preloadCount, props.isFirstLoad, props.isLoading, props.options]);

  // TODO: move to Timeline component
  React.useLayoutEffect(() => {
    const scrollEl = scrollRef.current;

    if (props.scrollTo && scrollEl) {
      const id = props.scrollTo === "last" ? last(items)?.id : props.scrollTo;
      if (id) {
        // TODO: figure out why not working in Helpdesk, fix and remove the timeout
        setTimeout(() => scrollToEl(`#${getHtmlId(id)}`, scrollEl), 0);
      }
    }
  }, [items, props.scrollTo]);

  const title = React.useMemo(() => {
    if (props.isNoTitle) return;
    if (isUndefined(props.filter)) return i18n.activity;
    if (typeof props.filter === "string") return props.filter;

    return (
      <SelectStatic title={props.filter.title} align={ContentAlign.Stretch} name={"filter"}>
        {props.filter.options.map((o) => (
          <SelectItem
            key={o.value.toString()}
            onClick={() => {
              (props.filter as ActivityFilter<T>).onChange(o.value);
            }}
            name={o.value.toString()}
          >
            {o.label}
          </SelectItem>
        ))}
      </SelectStatic>
    );
  }, [props.filter, props.isNoTitle]);

  return (
    <FormBlock
      ref={ref}
      actions={props.actions}
      className={classNames("activity", props.className)}
      isNarrow
      isBorderless={props.isBorderless}
      isFullHeight={props.isFullHeight}
      title={title}
      footerActions={props.footerActions}
      style={props.style}
      // IMPORTANT: don't pass in true to prevent height change and infinite refresh
      isLoading={props.isLoading && props.isFirstLoad ? "overlay" : false}
    >
      {!props.hasMore && !items.length ? (
        <div className="no_data">{props.noDataMessage ?? i18n.infoNoActivity}</div>
      ) : (
        <InfiniteScroll
          ref={scrollRef}
          className="scroll"
          element="div"
          loadMore={props.loadMore ?? identityPromise}
          hasMore={props.hasMore ?? false}
          onScroll={props.onScroll}
          loader={<Preloader isFontSize relative />}
          isLoading={props.isLoading === true}
        >
          <div className={classNames("items_wrap", { narrow: props.isNarrow })}>
            {props.isUngrouped ? (
              <Timeline hasLine={props.hasLine} items={items} dontSort isDateHidden={props.isDateHidden} />
            ) : (
              <TimelineGroupped hasLine={props.hasLine} items={items} isDateHidden={props.isDateHidden} />
            )}
            {props.itemsAddon && <span className="extra_data">{props.itemsAddon}</span>}
          </div>
        </InfiniteScroll>
      )}
    </FormBlock>
  );
};
export default Activity;

export const LazyActivity = withLazyHOC<ActivityItem, ActivityProps<any>>(Activity, (a, b) => a.id === b.id);
